import { deleteReq, get, post, put } from '../../../infra/rest';
import requestConfig from '../../../shared/config/request';
import { shouldHideDTDetails } from '../../../shared/utils/cpDetails';
import { VariableToAdd } from '../../../shared/utils/web-worker-functions';
import { AvailableChannelsResponse, BotListData, BotStatus, Integration, SubscribedPlan } from '../typings';
import { Api, ApiMethod, ApiRequestBodyFormat, ApiTestResponse } from '../typings/api';
import { APINodeData, AppData } from '../typings/bot-data';
import { convertDataToFormData } from '../utils/api';

interface SaveBotData {
  nodes: APINodeData[];
  edges: { source: string; target: string }[];
  variables?: { [k: string]: VariableToAdd[] };
  channelType?: string;
}

// Coalesces concurrent identical calls into one network request
const appDataInFlight = new Map<string, Promise<AppData>>();

export const getAppData = async (appId: string, channel?: string): Promise<AppData> => {
  const key = `${appId}:${channel ?? ''}`;
  const inFlight = appDataInFlight.get(key);
  if (inFlight) return inFlight;

  const params = channel ? `?channel=${channel}` : '';
  const request = get<AppData>(`/v1/bot-builder/${appId}/components${params}`, undefined, true).finally(() => {
    appDataInFlight.delete(key);
  });
  appDataInFlight.set(key, request);
  return request;
};

export const getAvailableChannels = async (): Promise<AvailableChannelsResponse> => {
  return get<AvailableChannelsResponse>(
    `${requestConfig.aiBotBaseUrl}/v1/conversation-channel/available`,
    undefined,
    true
  );
};

export const getBotList = async (): Promise<BotListData[]> => {
  return get(`/v2/bot/dt`, undefined, true);
};

export const createNewBot = async (data: { name: string }): Promise<BotListData> => {
  return post<BotListData>(
    `/v2/bot/dt`,
    {
      body: JSON.stringify(data),
    },
    true
  );
};

export async function deleteBot(id: string) {
  return await deleteReq(`/v1/bot/dt/${id}`, undefined, true);
}

export const saveBotData = async ({ data, botId, appId }: { botId: string; appId: string; data: SaveBotData }) => {
  return post(
    `/v1/bot-builder/${appId}/${botId}/flow`,
    {
      body: JSON.stringify(data),
    },
    true
  );
};

export const getBotFlow = async ({
  appId,
  botId,
}: {
  appId: string;
  botId: string;
}): Promise<{
  nodes: APINodeData[];
  edges: { source: { nodeId: string; uniqueId: string }; target: string }[];
  variables?: { [k: string]: VariableToAdd[] };
  bot: BotListData;
}> => {
  return get(`/v1/bot-builder/${appId}/${botId}/flow`, undefined, true);
};

export const updateBotData = async (
  botId: string,
  data: {
    name?: string;
    status?: BotStatus;
    channelType?: string;
  }
) => {
  return put(
    `/v2/bot/dt/${botId}`,
    {
      body: JSON.stringify(data),
    },
    true
  );
};

export const testNewApi = async (
  componentId: string,
  data: {
    url: string;
    method: ApiMethod;
    headers?: Record<string, string>;
    params?: Record<string, string>;
    pathParams?: Record<string, string>;
    body?: Record<string, string | number | boolean>;
    files?: Record<string, File[]>;
    bodyFormat?: ApiRequestBodyFormat;
  }
): Promise<ApiTestResponse> => {
  if (data.body && Object.keys(data.body).length > 0) {
    Object.keys(data.body).forEach((key) => {
      if (key === undefined || data.body![key] === undefined) {
        delete data.body![key];
      }
    });
    if (Object.keys(data.body).length === 0 || data.method === ApiMethod.HEAD) {
      delete data.body;
    }
  }
  if (data.params && Object.keys(data.params).length > 0) {
    Object.keys(data.params).forEach((key) => {
      if (!key || !data.params![key]) {
        delete data.params![key];
      }
    });
    if (Object.keys(data.params).length === 0) {
      delete data.params;
    }
  }
  if (data.pathParams && Object.keys(data.pathParams).length > 0) {
    Object.keys(data.pathParams).forEach((key) => {
      if (!key || !data.pathParams![key]) {
        delete data.pathParams![key];
      }
    });
    if (Object.keys(data.pathParams).length === 0) {
      delete data.pathParams;
    }
  }
  if (data.headers && Object.keys(data.headers).length > 0) {
    Object.keys(data.headers).forEach((key) => {
      if (!key || !data.headers![key]) {
        delete data.headers![key];
      }
    });
    if (Object.keys(data.headers).length === 0) {
      delete data.headers;
    }
  }

  const formData = convertDataToFormData(data);

  return post<ApiTestResponse>(
    `/v1/component-api/${componentId}/api-test`,
    {
      body: formData,
      useDefaultHeaders: false,
    },
    true
  );
};

export const createNewApi = async (
  componentId: string,
  data: {
    url: string;
    apiName: string;
    method: ApiMethod;
    headers?: Record<string, string>;
    params?: Record<string, string>;
    pathParams?: Record<string, string>;
    body?: Record<string, string | number | boolean>;
    files?: Record<string, File[]>;
    bodyFormat?: ApiRequestBodyFormat;
  }
): Promise<Api[]> => {
  if (data.body && Object.keys(data.body).length > 0) {
    Object.keys(data.body).forEach((key) => {
      if (key === undefined || data.body![key] === undefined) {
        delete data.body![key];
      }
    });
    if (Object.keys(data.body).length === 0 || data.method === ApiMethod.HEAD) {
      delete data.body;
    }
  }
  if (data.params && Object.keys(data.params).length > 0) {
    Object.keys(data.params).forEach((key) => {
      if (!key || !data.params![key]) {
        delete data.params![key];
      }
    });
    if (Object.keys(data.params).length === 0) {
      delete data.params;
    }
  }
  if (data.pathParams && Object.keys(data.pathParams).length > 0) {
    Object.keys(data.pathParams).forEach((key) => {
      if (!key || !data.pathParams![key]) {
        delete data.pathParams![key];
      }
    });
    if (Object.keys(data.pathParams).length === 0) {
      delete data.pathParams;
    }
  }
  if (data.headers && Object.keys(data.headers).length > 0) {
    Object.keys(data.headers).forEach((key) => {
      if (!key || !data.headers![key]) {
        delete data.headers![key];
      }
    });
    if (Object.keys(data.headers).length === 0) {
      delete data.headers;
    }
  }

  const formData = convertDataToFormData(data);

  return post<Api[]>(
    `/v1/component-api/${componentId}/api-add`,
    {
      body: formData,
      useDefaultHeaders: false,
    },
    true
  );
};

export const editCurrentApi = async (
  componentId: string,
  data: {
    url: string;
    apiName: string;
    method: ApiMethod;
    headers?: Record<string, string>;
    params?: Record<string, string>;
    pathParams?: Record<string, string>;
    body?: Record<string, string | number | boolean>;
    apiId: number;
    editable?: boolean;
    deletable?: boolean;
    files?: Record<string, File[]>;
    bodyFormat?: ApiRequestBodyFormat;
  }
): Promise<Api[]> => {
  if (data.body && Object.keys(data.body).length > 0) {
    Object.keys(data.body).forEach((key) => {
      if (key === undefined || data.body![key] === undefined) {
        delete data.body![key];
      }
    });
    if (Object.keys(data.body).length === 0 || data.method === ApiMethod.HEAD) {
      delete data.body;
    }
  }
  if (data.params && Object.keys(data.params).length > 0) {
    Object.keys(data.params).forEach((key) => {
      if (!key || !data.params![key]) {
        delete data.params![key];
      }
    });
    if (Object.keys(data.params).length === 0) {
      delete data.params;
    }
  }
  if (data.pathParams && Object.keys(data.pathParams).length > 0) {
    Object.keys(data.pathParams).forEach((key) => {
      if (!key || !data.pathParams![key]) {
        delete data.pathParams![key];
      }
    });
    if (Object.keys(data.pathParams).length === 0) {
      delete data.pathParams;
    }
  }
  if (data.headers && Object.keys(data.headers).length > 0) {
    Object.keys(data.headers).forEach((key) => {
      if (!key || !data.headers![key]) {
        delete data.headers![key];
      }
    });
    if (Object.keys(data.headers).length === 0) {
      delete data.headers;
    }
  }

  const formData = convertDataToFormData(data);

  return put<Api[]>(
    `/v1/component-api/${componentId}/api/${data.apiId}`,
    {
      body: formData,
      useDefaultHeaders: false,
    },
    true
  );
};

export async function deleteApi(apiId: Number, componentId: string) {
  return await deleteReq(`/v1/component-api/${componentId}/api/${apiId}`, undefined, true);
}

let _subscribedPlanPromise: Promise<{ subscribedPlan: SubscribedPlan }> | null = null;

export const getSubscribedPlan = () => {
  if (!_subscribedPlanPromise) {
    _subscribedPlanPromise = get<{
      subscribedPlan: SubscribedPlan;
    }>(
      `${shouldHideDTDetails() ? requestConfig.crmBaseUrlWL : requestConfig.crmBaseUrl}/v1/plan/subscription/status`,
      undefined,
      true
    ).finally(() => {
      _subscribedPlanPromise = null;
    });
  }
  return _subscribedPlanPromise;
};

export const getFlowTemplateVariables = (flowId: string) => {
  return get<Array<string>>(
    `${
      shouldHideDTDetails() ? requestConfig.crmBaseUrlWL : requestConfig.crmBaseUrl
    }/v1/template/flow-variables?flowId=${flowId}`,
    undefined,
    true
  );
};

export const getIntentFilterParameters = async (intentFilterId: string) => {
  return await get<{
    parameters: Array<{ name: string }>;
    hasPassToolCallResponse: boolean;
    isJourneyBotEnabled: boolean;
  }>(`${requestConfig.aiBotBaseUrl}/v1/intent-filter/${intentFilterId}`, undefined, true);
};

export const getMimeTypeFromUrl = async (
  url: string
): Promise<{
  mimeType: string;
}> => {
  return get<{
    mimeType: string;
  }>(`${requestConfig.aiBotBaseUrl}/v1/internal/url-mime-type?url=${url}`, undefined, true);
};

export const getAllIntegrations = async (): Promise<Integration[]> => {
  const integrations = await get<Integration[]>(
    `${shouldHideDTDetails() ? requestConfig.crmBaseUrlWL : requestConfig.crmBaseUrl}/v2/organization/integration`,
    undefined,
    true
  );
  return integrations ?? [];
};
